import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { downloadAgreementPdf } from "../lib/downloadPdf";
import { CheckCircle2, XCircle, Loader2, FileDown } from "lucide-react";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling, paid, expired, error
  const [bookingId, setBookingId] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setBookingId(data.booking_id);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= 8) {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="px-6 md:px-12 lg:px-20 py-24 text-center max-w-2xl mx-auto" data-testid="booking-success-page">
      {status === "polling" && (
        <>
          <Loader2 size={48} className="mx-auto animate-spin text-neutral-400" />
          <p className="overline mt-8">Confirming…</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Processing your payment</h1>
          <p className="mt-4 text-neutral-600">This usually takes a few seconds. Please don't close this window.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={56} className="mx-auto text-[#004B35]" />
          <p className="overline mt-8" style={{ color: "#004B35" }}>Confirmed</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3 tracking-tight">
            You're on the <em className="italic">road</em>.
          </h1>
          <p className="mt-4 text-neutral-600">
            Your reservation is confirmed. A concierge will contact you 24 hours before pickup.
          </p>
          <div className="mt-10 flex gap-4 justify-center flex-wrap">
            <Link to="/dashboard" className="bg-black text-white px-7 py-3 text-sm hover:bg-neutral-800 transition-colors" data-testid="goto-dashboard-button">
              View my bookings
            </Link>
            {bookingId && (
              <button
                type="button"
                onClick={() => downloadAgreementPdf(bookingId)}
                className="inline-flex items-center gap-2 border border-black/20 hover:border-black px-7 py-3 text-sm transition-colors"
                data-testid="success-download-pdf"
              >
                <FileDown size={14} /> Download Agreement
              </button>
            )}
            <Link to="/vehicles" className="border border-black/20 hover:border-black px-7 py-3 text-sm transition-colors">
              Browse fleet
            </Link>
          </div>
        </>
      )}
      {(status === "expired" || status === "error") && (
        <>
          <XCircle size={56} className="mx-auto text-red-600" />
          <p className="overline mt-8 text-red-600">Unsuccessful</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Payment didn't complete</h1>
          <p className="mt-4 text-neutral-600">The session expired or was cancelled. Your booking is still saved as pending — you can retry checkout from your dashboard.</p>
          <div className="mt-10 flex gap-4 justify-center">
            {bookingId && (
              <Link to={`/booking/${bookingId}`} className="bg-black text-white px-7 py-3 text-sm hover:bg-neutral-800 transition-colors">
                Retry payment
              </Link>
            )}
            <Link to="/dashboard" className="border border-black/20 hover:border-black px-7 py-3 text-sm transition-colors">
              View bookings
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
